/**
 * Hand-written response types for the TOS multi-doc admin API.
 *
 * Backend doesn't annotate response models with `@ApiProperty` today,
 * so Orval emits `{ [key: string]: unknown }` for every list/detail
 * response (see TD-07 / the same pattern in `venues-approval/types.ts`).
 * Until backend ships the annotations, these mirror the actual JSON
 * shape returned by the controllers and use cases in
 * `wanderfit-api/src/tos/`.
 *
 * Source of truth verified against:
 *   - `wanderfit-api/src/tos/domain/tos.repository.ts`           (TermsOfService, TosTranslation)
 *   - `wanderfit-api/src/tos/domain/tos-document-type.repository.ts`  (TosDocumentType)
 *   - `wanderfit-api/src/tos/domain/locale.repository.ts`            (SupportedLocale)
 *   - `wanderfit-api/src/tos/domain/tos-role-requirement.repository.ts` (TosRoleRequirement)
 *
 * Date fields in the wire shape are ISO strings (JSON-serialised
 * `Date` objects). All collection types are `readonly` per the
 * project's immutability rule.
 */

/**
 * FALLBACK ONLY. The list of role scopes the BO renders comes from the
 * API via `useTosRoleScopes()` (`shared/use-tos-role-scopes.ts`); this
 * constant is what that hook returns while the request is loading or
 * has failed, and it seeds the ordering (known scopes first). It
 * mirrors `TOS_ROLE_SCOPES` in
 * `wanderfit-api/src/tos/domain/role-scope.ts` at the time of writing
 * (ADR-0017 A6 added `STAFF`). Do NOT iterate it from a component — a
 * scope the API adds later must show up without a BO release.
 */
export const TOS_ROLE_SCOPES = ["GUEST", "PROPERTY_OWNER", "VENUE_OWNER", "STAFF"] as const;

/**
 * A role scope key as the API reports it. The known literals are kept
 * for autocomplete; the `string & {}` arm lets any scope the API ships
 * later flow through typed fields (`roleScope`, `role`) unchanged.
 */
export type TosRoleScope = (typeof TOS_ROLE_SCOPES)[number] | (string & {});

export interface TosDocumentType {
  readonly id: string;
  readonly key: string;
  readonly name: string;
  /** Null when unset; the API declares it optional. */
  readonly description?: string | null;
  readonly isSystem: boolean;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface SupportedLocale {
  readonly code: string;
  readonly displayName: string;
  readonly isCanonical: boolean;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface TosRoleRequirement {
  readonly role: TosRoleScope;
  readonly documentTypeId: string;
  readonly createdAt: string;
}

export interface TosTranslation {
  readonly id: string;
  readonly tosId: string;
  readonly localeCode: string;
  readonly title: string;
  readonly bodyMarkdown: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/**
 * Backend's explicit lifecycle state. Derived server-side from the
 * `(effectiveFrom, effectiveUntil)` pair so the FE doesn't reproduce
 * the date math.
 *
 *   DRAFT             effectiveUntil === effectiveFrom (never published yet; mutable)
 *   ACTIVE            effectiveUntil === null          (live; mutable for typo fixes)
 *   SCHEDULED_RETIRE  effectiveUntil > now             (live AND has a future retire date)
 *   RETIRED           effectiveUntil <= now            (history; immutable)
 */
export const TOS_STATES = ["DRAFT", "ACTIVE", "SCHEDULED_RETIRE", "RETIRED"] as const;

export type TosState = (typeof TOS_STATES)[number];

export interface TermsOfService {
  readonly id: string;
  readonly roleScope: TosRoleScope;
  readonly documentTypeId: string;
  /**
   * Denormalised on list + detail responses — saves a join client-side. Null when
   * the document type could not be resolved; the API declares both optional.
   */
  readonly documentTypeKey?: string | null;
  readonly documentTypeName?: string | null;
  readonly version: string;
  readonly state: TosState;
  readonly effectiveFrom: string;
  /** Null while ACTIVE; the API declares it optional. */
  readonly effectiveUntil?: string | null;
  readonly createdAt: string;
  /** List response: locale codes only (lean). Detail: full translations[]. */
  readonly locales?: readonly string[];
  readonly translations?: readonly TosDocumentTranslation[];
}

/** A translation as embedded in the document detail (`AdminTosTranslationView`): no `tosId`. */
export type TosDocumentTranslation = Omit<TosTranslation, "tosId">;

export interface RetireTosInput {
  readonly effectiveUntil: string;
}

// ── Request inputs ────────────────────────────────────────────────

export interface CreateDocumentTypeInput {
  readonly key: string;
  readonly name: string;
  readonly description?: string;
}

export interface UpdateDocumentTypeInput {
  readonly name?: string;
  readonly description?: string;
}

export interface CreateLocaleInput {
  readonly code: string;
  readonly displayName: string;
  readonly isCanonical?: boolean;
}

export interface UpdateLocaleInput {
  readonly displayName?: string;
  readonly isCanonical?: boolean;
}

export interface AddRoleRequirementInput {
  readonly role: TosRoleScope;
  readonly documentTypeId: string;
}

export interface TosTranslationInput {
  readonly localeCode: string;
  readonly title: string;
  readonly bodyMarkdown: string;
}

export interface CreateTosInput {
  readonly roleScope: TosRoleScope;
  readonly documentTypeId: string;
  readonly version: string;
  readonly effectiveFrom: string;
  readonly translations: readonly TosTranslationInput[];
}

export interface AddTranslationInput {
  readonly localeCode: string;
  readonly title: string;
  readonly bodyMarkdown: string;
}

export interface UpdateTranslationInput {
  readonly title?: string;
  readonly bodyMarkdown?: string;
}

// ── Backend body-length contracts (mirror wanderfit-api DTOs) ─────

export const TOS_TRANSLATION_TITLE_MAX = 200;
export const TOS_TRANSLATION_BODY_MAX = 200_000;
export const TOS_VERSION_MAX = 40;
export const DOCUMENT_TYPE_KEY_MAX = 64;
export const DOCUMENT_TYPE_NAME_MAX = 200;
export const DOCUMENT_TYPE_DESCRIPTION_MAX = 1000;
export const LOCALE_CODE_MAX = 35;
export const LOCALE_DISPLAY_NAME_MAX = 100;
